import React from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { HandsSwapped } from "@uno/protocol";
import Card from "component/Card/Card";
import useAnimation from "hook/useAnimation";
import useStoreAnimation from "store/useStoreAnimation";
import { SWAP_HANDS_ANIMATION_ID } from "animation/SwapHandsAnimation";

const STACK_SIZE = 3;

const SwapHandsOverlay = () => {
    const { t } = useTranslation();
    const ref = React.useRef<HTMLDivElement>(null);
    const payload = useStoreAnimation(state => state.animations[SWAP_HANDS_ANIMATION_ID]?.[0]?.payload) as HandsSwapped | undefined;

    useAnimation({ id: SWAP_HANDS_ANIMATION_ID, ref });

    return ReactDOM.createPortal(
        <div ref={ref} className="fixed inset-0 z-[60] pointer-events-none">
            {payload?.swaps.map((swap, index) => (
                <div key={`${swap.from}-${swap.to}`} data-hand-swap={index} className="absolute left-0 top-0 opacity-0">
                    <div className="relative">
                        {Array.from({ length: Math.min(STACK_SIZE, Math.max(1, swap.count)) }).map((_, card) => (
                            <div key={card} className="absolute" style={{ transform: `translate(${card * 6}px, ${card * -4}px) rotate(${(card - 1) * 6}deg)` }}>
                                <Card id={`swap-${index}-${card}`} type="BACK" isHoverable={false} />
                            </div>
                        ))}
                        <span className="absolute -top-4 -right-6 z-10 min-w-8 px-2 py-0.5 rounded-full bg-white text-gray-900 text-lg font-changa-one text-center shadow">
                            {swap.count}
                        </span>
                    </div>
                </div>
            ))}

            <div className="absolute inset-x-0 top-[30%] flex justify-center">
                <AnimatePresence>
                    {payload && (
                        <motion.div
                            key="swap-hands-label"
                            initial={{ opacity: 0, scale: 0.7 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                            className="px-6 py-2.5 rounded-full whitespace-nowrap bg-gradient-to-b from-yellow-300 to-amber-500 border-2 border-yellow-100/50 shadow-[0_0_20px_rgba(251,191,36,0.5)]"
                        >
                            <span className="font-changa-one text-lg text-white uppercase drop-shadow-md">
                                {t(payload.kind === "seven" ? "room.swap.seven" : "room.swap.zero")}
                            </span>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>,
        document.body,
    );
};

export default SwapHandsOverlay;
